import { useEffect, useState } from 'react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Analytics helper functions for tracking events
export const trackEvent = (eventName, eventParams = {}) => {
  // Google Analytics 4
  if (window.gtag) {
    window.gtag('event', eventName, eventParams);
  }
  
  // Plausible
  if (window.plausible) {
    window.plausible(eventName, { props: eventParams });
  }
};

// Track contact form submission as conversion
export const trackContactFormSubmission = (formData) => {
  trackEvent('contact_form_submission', {
    interest: formData.interest || 'general',
    event_category: 'engagement',
    event_label: 'Contact Form',
    value: 1
  });
  
  // GA4 specific conversion tracking
  if (window.gtag) {
    window.gtag('event', 'conversion', {
      send_to: 'conversion',
      event_category: 'Contact',
      event_label: formData.interest || 'general'
    });
  }
};

// Track page views
export const trackPageView = (pagePath, pageTitle) => {
  if (window.gtag) {
    window.gtag('event', 'page_view', {
      page_path: pagePath,
      page_title: pageTitle
    });
  }
  
  if (window.plausible) {
    window.plausible('pageview');
  }
};

const Analytics = () => {
  const [settings, setSettings] = useState(null);
  const [loaded, setLoaded] = useState({ ga4: false, plausible: false });

  useEffect(() => {
    const fetchSettings = async () => {
      try {
        const response = await fetch(`${API_URL}/api/public/analytics-settings`);
        if (response.ok) {
          const data = await response.json();
          setSettings(data);
        }
      } catch (error) {
        console.log('Could not load analytics settings');
      }
    };
    fetchSettings();
  }, []);

  useEffect(() => {
    if (!settings) return;

    // Load Google Analytics 4
    if (settings.ga4_enabled && settings.ga4_measurement_id && !loaded.ga4) {
      const measurementId = settings.ga4_measurement_id;
      
      // Load gtag.js script
      const gtagScript = document.createElement('script');
      gtagScript.async = true;
      gtagScript.src = `https://www.googletagmanager.com/gtag/js?id=${measurementId}`;
      document.head.appendChild(gtagScript);

      // Initialize gtag
      window.dataLayer = window.dataLayer || [];
      window.gtag = function() { window.dataLayer.push(arguments); };
      window.gtag('js', new Date());
      window.gtag('config', measurementId, {
        page_path: window.location.pathname,
        anonymize_ip: true
      });

      setLoaded(prev => ({ ...prev, ga4: true }));
      console.log('Google Analytics 4 loaded:', measurementId);
    }

    // Load Plausible Analytics
    if (settings.plausible_enabled && settings.plausible_domain && !loaded.plausible) {
      const plausibleScript = document.createElement('script');
      plausibleScript.defer = true;
      plausibleScript.dataset.domain = settings.plausible_domain;
      plausibleScript.src = 'https://plausible.io/js/script.js';
      document.head.appendChild(plausibleScript);

      // Initialize plausible function
      window.plausible = window.plausible || function() {
        (window.plausible.q = window.plausible.q || []).push(arguments);
      };

      setLoaded(prev => ({ ...prev, plausible: true }));
      console.log('Plausible Analytics loaded:', settings.plausible_domain);
    }
  }, [settings, loaded]);

  return null; // This component doesn't render anything
};

export default Analytics;
